/* eslint-disable max-len */
import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import filter from 'lodash/filter';
import uniqBy from 'lodash/uniqBy';
import difference from 'lodash/difference';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import { SnackBarHelper } from '@lyearn-team/frontend-components';

import { AssetType, DataType } from '../../appConsts';
import CurrentUser from '../currentUser';

import * as LocalStoreService from './LocalStore';
import request from '../NetworkLayer';

export const LYEARN_HOME_PAGE_DATA = '/homepage';
export const LYEARN_CONTENT_CLOUDFRONT_URL = 'https://d2t3p1sttnotqb.cloudfront.net';
export const LYEARN_CONTENT_S3_URL = 'https://s3.amazonaws.com/lyearn-content';
export const LYEARN_STATES_DYNAMO = '/fetch-states';
export const LYEARN_COMPLETE_DATA = '/fetch-complete-data';
export const LYEARN_ADMIN_ASSETS_DATA = '/admin-asset-query';
export const LYEARN_DATASYNC_STATUS = '/fetch-datasync-status';
export const LYEARN_DYNANO_DATA = '/fetch-dynamo-data';

const user = new CurrentUser();

async function getUserManagesIds(userId) {
  let manageIds = [];
  let allGroupIds = [];
  const userFromIDB = await LocalStoreService.getUserById(userId);
  if (userFromIDB && userFromIDB.manages && userFromIDB.manages.length > 0) {
    manageIds = compact(userFromIDB.manages.map(u => u.pid));
  }
  if (userFromIDB) {
    allGroupIds = userFromIDB.allGroupIds;
  }
  return { manageIds, allGroupIds };
}

export async function getOfferFromS3(offerId) {
  try {
    const response = await fetch(`${LYEARN_CONTENT_S3_URL}/${offerId}.json`);
    if (response.ok) {
      const offer = await response.json();
      LocalStoreService.saveOffer(offer);
      return offer;
    }
    console.log('Error in fetching offer from s3, response is', response);
    throw Error('Error in fetching offer from s3');
  } catch (e) {
    console.log('Error in fetching offer from s3', e);
    e.name = 'ERROR_IN_FETCH_FROM_S3';
    window.bugsnagClient && window.bugsnagClient.notify(e);
    window.analytics &&
      window.analytics.track('ERROR_IN_FETCH_FROM_S3', {
        offerId,
        error: e.message,
        errorType: e.type,
        errorStack: e.stack
      });
    throw e;
  }
}

/*                        FETCH DATA REMOTELY HELPERS                            */

export async function fetchLatestOfferFromCloudfront(offerId) {
  try {
    const response = await fetch(`${LYEARN_CONTENT_CLOUDFRONT_URL}/${offerId}.json`);
    if (response.ok) {
      const offer = await response.json();
      LocalStoreService.saveOffer(offer);
      return offer;
    }
    console.log('Error in fetching offer from Cloudfront, response is', response);
    throw Error('Error in fetching offer from Cloudfront');
  } catch (error) {
    console.log('Error in fetching offer from Cloudfront', error);
    error.name = 'ERROR_IN_FETCH_FROM_CLOUDFRONT';
    window.analytics &&
      window.analytics.track('ERROR_IN_FETCH_FROM_CLOUDFRONT', {
        offerId,
        error: error.message,
        errorType: error.type,
        errorStack: error.stack
      });
    // Fetch form S3 when failed from CloudFront
    try {
      const response = await fetch(`${LYEARN_CONTENT_S3_URL}/${offerId}.json`);
      if (response.ok) {
        const offer = await response.json();
        LocalStoreService.saveOffer(offer);
        return offer;
      }
      console.log('Error in fetching offer from s3, response is', response);
      throw Error('Error in fetching offer from s3');
    } catch (e) {
      console.log('Error in fetching offer from s3', e);
      e.name = 'ERROR_IN_FETCH_FROM_S3';
      window.bugsnagClient && window.bugsnagClient.notify(e);
      window.analytics &&
        window.analytics.track('ERROR_IN_FETCH_FROM_S3', {
          offerId,
          error: e.message,
          errorType: e.type,
          errorStack: e.stack
        });
      throw error;
    }
  }
}

export async function getOfferByIdAndVersionIdRemotely(offerId, versionId) {
  if (!versionId) {
    return fetchLatestOfferFromCloudfront(offerId);
  }
  try {
    const response = await fetch(`${LYEARN_CONTENT_S3_URL}/${offerId}.json?versionId=${versionId}`);
    if (!response.ok) {
      const responseText = await response.text();
      throw new Error('Error fetching offer by versionId from s3 ' + responseText);
    }
    const offer = await response.json();
    LocalStoreService.saveOffer(offer);
    return offer;
  } catch (error) {
    console.log('Error fetching offer by versionId from s3');
    console.log(error);
    if (error.message.includes('Failed to fetch')) {
      error.name = 'FAILED_TO_FETCH_ERROR_IN_FETCH_BY_VERSIONID_FROM_S3';
    } else {
      error.name = 'ERROR_IN_FETCH_BY_VERSIONID_FROM_S3';
    }
    window.analytics &&
      window.analytics.track('ERROR_IN_FETCH_BY_VERSIONID_FROM_S3', {
        offerId,
        versionId,
        error: error.message,
        errorType: error.type,
        errorStack: error.stack
      });
    window.bugsnagClient && window.bugsnagClient.notify(error);
    throw error;
  }
}

export async function getOfferByIdLocally(offerId) {
  return LocalStoreService.getOffer(offerId);
}

export async function getMultipleOfferByIdLocally(offerIds) {
  return Promise.all(offerIds.map(oId => getOfferByIdLocally(oId)));
}

/*                        Fetch data locally-first helpers                  */
export async function getOfferByIdLocallyOrRemotely(offerId) {
  const localOffer = await LocalStoreService.getOffer(offerId);
  if (localOffer) return localOffer;

  return fetchLatestOfferFromCloudfront(offerId);
}

export function getMultipleOfferByIdAndVersionIdsRemotely(offerIdVersionIds) {
  return Promise.all(
    offerIdVersionIds.map(o => getOfferByIdAndVersionIdRemotely(o.offerId, o.versionId))
  );
}

/*                      DAta sync status check                  */

async function saveDocumentsNotInDataSync(
  documents,
  keyAttribute,
  dsoKey,
  localStoreMultiGet,
  localStoreMultiSet
) {
  const docById = keyBy(filter(documents), keyAttribute);
  const inputDocIds = Object.keys(docById);

  const dataSyncObjects = await LocalStoreService.getAllDataSyncObjects();
  const dataSyncObjectDocIds = flatten(dataSyncObjects.map(dso => dso[dsoKey] || []));

  const notInDSODocIds = difference(inputDocIds, dataSyncObjectDocIds);

  const existingDocs = filter(await localStoreMultiGet(notInDSODocIds));
  const existingDocsById = keyBy(existingDocs, keyAttribute);

  const greaterVersionDocIds = notInDSODocIds.filter(docId => {
    const currentDoc = existingDocsById[docId];
    if (!currentDoc) return true;
    const incomingDoc = docById[docId];
    return incomingDoc.versionNumber > currentDoc.versionNumber;
  });

  const greaterVersionDocs = greaterVersionDocIds.map(key => docById[key]);

  return localStoreMultiSet(greaterVersionDocs);
}

export async function saveStatesNotInDataSync(states) {
  return saveDocumentsNotInDataSync(
    states,
    doc => `${doc.userId}_${doc.contentId}`,
    'stateIds',
    LocalStoreService.getMultipleStatesById,
    LocalStoreService.saveStates
  );
}

export async function saveEnrolmentsNotInDataSync(enrolments) {
  return saveDocumentsNotInDataSync(
    enrolments,
    doc => `${doc.userId}_${doc.contentId}`,
    'enrollmentIds',
    LocalStoreService.getMultipleEnrolmentsById,
    LocalStoreService.saveAllEnrolments
  );
}

export async function saveStateInteractionsNotInDataSync(stateInteractions) {
  return saveDocumentsNotInDataSync(
    stateInteractions,
    'contentId_interactionType_interactingOnId_actorId',
    'stateInteractionIds',
    LocalStoreService.getMultipleStateInteractionsById,
    LocalStoreService.saveStateInteractions
  );
}

export async function getDataSyncStatus(dataSyncInfo) {
  if (!dataSyncInfo || dataSyncInfo.length < 1) {
    return [];
  }
  try {
    const response = await request(`${LYEARN_DATASYNC_STATUS}`, {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        dataSyncParams: dataSyncInfo
      })
    }).then(r => r.json());
    if (!Array.isArray(response)) {
      console.log('Error response from fetch-datasync-status', dataSyncInfo, response);
      const error = new Error('fetch-datasync-status returned non-array result');
      error.name = 'FETCH_DATASYNC_STATUS_ERROR';
      throw new Error(error);
    }
    return response;
  } catch (e) {
    console.log('Error fetch-datasync-status', JSON.stringify(dataSyncInfo));
    console.log('Error in fetch-datasync-status', e);
    window.bugsnagClient.notify(e, { metaData: dataSyncInfo });
    return [];
  }
}

// Admin side query
export async function fetchAdminAssets(item) {
  return request(`${LYEARN_ADMIN_ASSETS_DATA}`, {
    method: 'POST',
    headers: { 'Content-type': 'application/json; charset=utf-8' },
    body: JSON.stringify(item)
  });
}

export async function fetchStatesRemotelyFromUserIdContentIdObject(stateObjects) {
  try {
    window.analytics &&
      window.analytics.track('HOMEPAGE_FETCH_DATA', {
        stateObjects,
        stateObjectsString: JSON.stringify(stateObjects)
      });
  } catch (e) {
    console.error(e);
  }

  const response = await request(
    `${LYEARN_STATES_DYNAMO}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify(stateObjects)
    },
    {
      dontAppendApiEndpointQS: true,
      dontSendAuthHeader: true
    }
  ).then(r => r.json());
  if (!response.success) {
    console.log('fetch states dynamo response is', response);
    throw new Error('Error in fetching states from dynamo');
  }

  const states = response.data;
  await LocalStoreService.saveStates(states);
  return states;
}

export async function getStateInteractionByOfferId(offerId) {
  const allStateInteractions = await LocalStoreService.getAllStateInteractions();
  if (!allStateInteractions) {
    return [];
  }
  return allStateInteractions.filter(
    stateInteraction => stateInteraction.rootContentId === offerId
  );
}

function fetchContentDataGuest(assetId, userId, assetType, children, manageIds, allGroupIds = []) {
  return fetch(
    `https://prod-api.lyearn.com${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        userId,
        manageIds,
        assetData: [
          {
            assetId,
            assetType,
            userGroupIds: allGroupIds,
            shouldMergeContentSettings: true
          }
        ],
        children
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
}

export function fetchContentData(
  assetId,
  userId,
  assetType,
  children,
  manageIds,
  allGroupIds = []
) {
  try {
    window.analytics &&
      window.analytics.track('FETCH_COMPLETE_DATA', {
        userId,
        assetId,
        assetType,
        pageUrl: window.location.href
      });
  } catch (e) {
    console.error(e);
  }
  return request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        userId,
        manageIds,
        assetData: [
          {
            assetId,
            assetType,
            userGroupIds: allGroupIds,
            shouldMergeContentSettings: true
          }
        ],
        children
      })
    },
    {
      dontAppendApiEndpointQS: true,
      dontSendAuthHeader: true
    }
  );
}

export async function getStatesEnrolmentsStateInteractionsForOfferLocally(offerId, userId) {
  const enrolment = await LocalStoreService.getEnrolment(userId, offerId);
  const states = await LocalStoreService.getStatesForAsset(userId, offerId);
  const allStateInteractions = await getStateInteractionByOfferId(offerId);
  const contentSettings = await LocalStoreService.getContentSettingsByIds([offerId]);
  const contentDetails = await LocalStoreService.getContentDetailsByContentId(offerId);

  const stateInteractions = allStateInteractions.filter(interaction => !interaction.deleted);
  const parentIds = enrolment && enrolment.parentIds ? enrolment.parentIds : [];
  const parentOffers = filter(await getMultipleOfferByIdLocally(parentIds));
  const parentOfferStates = await LocalStoreService.getMultipleStatesByUserIdContentIds(
    userId,
    parentIds
  );

  const nonExistsStates = parentOffers.filter(item => !item);
  if (nonExistsStates.length) {
    const missingStates = {
      parentIds,
      parentOfferStates
    };
    window.bugsnagClient.notify(new Error('parentOfferStates are missing'), {
      metaData: missingStates
    });
  }

  const parentChildrenIds = flatten(
    parentOffers.map(cOffer => cOffer.offers.map(offer => offer.offerId))
  );
  const parentChildrenStates = await LocalStoreService.getMultipleStatesByUserIdContentIds(
    userId,
    parentChildrenIds
  );

  return {
    enrolments: enrolment ? [enrolment] : [],
    states: uniqBy([...states, ...parentOfferStates, ...parentChildrenStates], 'userId_contentId'),
    stateInteractions,
    contentSettings,
    contentDetails
  };
}

export async function getStatesEnrolmentsStateInteractionsForOfferRemotely(
  offerId,
  userId,
  overwriteStates
) {
  const response = await fetchContentData(offerId, userId || user.id(), AssetType.offer, true);
  const {
    states,
    curriculumStates,
    enrolments,
    stateInteractions,
    contentSettings,
    contentDetails
  } = await response.json();

  if (typeof enrolments === 'undefined') {
    console.log(
      'Not received enrolments - OfferId, UserId, AssetType',
      offerId,
      userId,
      AssetType.offer
    );
  }

  await Promise.all([
    contentSettings && contentSettings.length
      ? LocalStoreService.saveAllContentSettings(contentSettings)
      : Promise.resolve(),
    states && states.length
      ? overwriteStates
        ? await LocalStoreService.saveStates(states)
        : saveStatesNotInDataSync(states)
      : Promise.resolve(),
    curriculumStates && curriculumStates.length
      ? saveStatesNotInDataSync(curriculumStates)
      : Promise.resolve(),
    enrolments && enrolments.length ? saveEnrolmentsNotInDataSync(enrolments) : Promise.resolve(),
    stateInteractions && stateInteractions.length
      ? saveStateInteractionsNotInDataSync(stateInteractions)
      : Promise.resolve(),
    contentDetails
      ? LocalStoreService.saveContentDetails(offerId, contentDetails)
      : Promise.resolve()
  ]);

  return {
    enrolments,
    states: [...(states || []), ...(curriculumStates || [])],
    stateInteractions,
    contentSettings,
    contentDetails
  };
}

export async function fetchTopRatedResponses(assetId) {
  return request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        assetData: [
          {
            assetId,
            assetType: 'TOP_RATED_RESPONSE',
            offset: 0,
            limit: 3,
            companyId: user.companyId()
          }
        ]
      })
    },
    {
      dontAppendApiEndpointQS: true,
      dontSendAuthHeader: true
    }
  );
}

export async function fetchNextSubmissionReview(assetId, userId, limit, reviewedUserIds) {
  return request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        userId,
        assetData: [
          {
            assetId,
            assetType: 'NEXT_SUBMISSION_TO_REVIEW',
            offset: 0,
            limit,
            reviewedUserIds,
            companyId: user.companyId()
          }
        ]
      })
    },
    {
      dontAppendApiEndpointQS: true,
      dontSendAuthHeader: true
    }
  );
}

/*                        FETCH DATA LOCALLY HELPERS                            */

export async function getEnrolmentForOfferLocally(userId, assetId) {
  const enrolment = await LocalStoreService.getEnrolment(userId, assetId);
  return enrolment;
}

/*                        USER LIBRARY CONTENT PIDS                            */
export async function fetchUserLibraryContentPidsLocally() {
  const libraryContentPids = await LocalStoreService.getAllLibraryContentPids();
  return libraryContentPids;
}

export async function fetchUserLibraryContentPidsRemotely(userId) {
  const response = await request(
    `${LYEARN_HOME_PAGE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        userId,
        dataType: DataType.fullLibrary
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  if (response.ok) {
    const fullLibraryContentPids = await response.json();
    await LocalStoreService.saveAllLibraryContentPids(fullLibraryContentPids);
  }
  return fetchUserLibraryContentPidsLocally();
}

// Create function for Local fetch

export async function getContentSettingsByContentListLocally(contentList, includeArray, userId) {
  const contentSettingsForContentList = {
    enrolmentsData: [],
    statesData: [],
    contentSettingsData: []
  };
  if (includeArray.includes('enrolments')) {
    const keys = [];
    contentList.forEach(content => {
      keys.push(`${userId}_${content.contentId}`);
    });
    const enrolments = await LocalStoreService.getMultipleEnrolmentsById(keys);
    contentSettingsForContentList.enrolmentsData = enrolments;
  }
  if (includeArray.includes('states')) {
    const keys = [];
    contentList.forEach(content => {
      keys.push(`${userId}_${content.contentId}`);
    });
    const states = await LocalStoreService.getMultipleStatesById(keys);
    contentSettingsForContentList.statesData = states;
  }
  if (includeArray.includes('contentSettings')) {
    const keys = [];
    contentList.forEach(content => {
      keys.push(content.contentId);
    });
    const contentSettings = await LocalStoreService.getContentSettingsByIds(keys);
    contentSettingsForContentList.contentSettingsData = contentSettings;
  }
  return contentSettingsForContentList;
}

export async function getContentSettingsByContentList(contentList, includeArray, userId) {
  let contentSettingsForContentList = {
    enrolmentsData: [],
    statesData: [],
    contentSettingsData: []
  };
  try {
    let finalContentList = contentList;
    if (userId) {
      finalContentList = contentList.map(content => ({
        ...content,
        userId
      }));
    }
    const response = await request(`${LYEARN_DYNANO_DATA}`, {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        contentList: finalContentList,
        includeArray,
        shouldMergeContentSettings: true
      })
    });
    if (response.ok) {
      contentSettingsForContentList = await response.json();
      const promiseList = [];
      if (includeArray.includes('enrolments')) {
        promiseList.push(saveEnrolmentsNotInDataSync(contentSettingsForContentList.enrolmentsData));
      }
      if (includeArray.includes('states')) {
        promiseList.push(saveStatesNotInDataSync(contentSettingsForContentList.statesData));
      }
      if (includeArray.includes('contentSettings')) {
        promiseList.push(
          LocalStoreService.saveAllContentSettings(
            contentSettingsForContentList.contentSettingsData
          )
        );
      }
      await Promise.all(promiseList);
      return contentSettingsForContentList;
    }
  } catch (error) {
    console.log('Erro -> ', error);
  }
  return getContentSettingsByContentListLocally(contentList, includeArray, userId);
}

/*                        Calendar Data                            */

export async function fetchCalendarDataLocally(userId) {
  const allOffers = await LocalStoreService.getAllOffers();
  const allILTs = allOffers.filter(o => o.activityType === 'liveTraining');
  const allStates = await Promise.all(
    allILTs.map(ilt => LocalStoreService.getStateByUserIdContentId(userId, ilt._id))
  );
  const allEnrolments = await Promise.all(
    allILTs.map(ilt => LocalStoreService.getEnrolment(userId, ilt._id))
  );

  return {
    contents: allILTs,
    allSessionDetails: [],
    states: allStates,
    enrolments: allEnrolments
  };
}

export async function fetchCalendarDataRemotely(userId) {
  const response = await request(
    `${LYEARN_HOME_PAGE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        userId,
        dataType: DataType.calendar
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  if (response.ok) {
    const calendarData = await response.json();
    return calendarData;
  }
  return null;
}

/*                        COMPANY CUSTOM PROPERTIES                            */
export async function fetchCompanyCustomPropertiesLocally() {
  const customPropertyResponse = await LocalStoreService.getAllCustomProperties();
  return customPropertyResponse;
}

export async function fetchCompanyCustomPropertiesRemotely(companyId) {
  const response = await request(
    `${LYEARN_HOME_PAGE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        companyId,
        dataType: DataType.customProperties
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  let customPropertiesResponse;
  if (response.ok) {
    customPropertiesResponse = await response.json();
    if (!customPropertiesResponse || !Array.isArray(customPropertiesResponse)) {
      console.log('customProperties missing ', customPropertiesResponse);
    }
    customPropertiesResponse &&
      (await LocalStoreService.saveAllCustomProperties(customPropertiesResponse));
  }
  customPropertiesResponse = await fetchCompanyCustomPropertiesLocally();
  return customPropertiesResponse;
}

export async function fetchCompanyCustomProperties(companyId, refetch) {
  let customPropertyResponse = await LocalStoreService.getAllCustomProperties();
  if (
    (!customPropertyResponse || customPropertyResponse.length < 1) &&
    (typeof refetch === 'boolean' || refetch === true)
  ) {
    customPropertyResponse = await fetchCompanyCustomPropertiesRemotely(companyId);
  }
  return customPropertyResponse;
}

/*                        HOMEPAGE                            */

export async function fetchHomePageDataLocally(userId) {
  let homePageResponse = {
    enrolmentData: [],
    stateData: [],
    contentSettings: [],
    stateInteractionData: []
  };

  // Feature: includeInRecommendation
  // Check if some ContentSettings has includeInRecommendation as `true`
  const allContentSettings = await LocalStoreService.getAllContentSetting();
  const recommendedCS = allContentSettings.filter(
    contentSetting => contentSetting.includeInRecommendation
  );
  let recommendedCSIds = [];
  if (recommendedCS.length > 0) {
    recommendedCSIds = recommendedCS.map(rcs => rcs.contentId);
  }

  const allEnrolments = await LocalStoreService.getAllEnrolments();
  if ((allEnrolments && allEnrolments.length > 0) || recommendedCSIds.length > 0) {
    const stateIds = [];
    let contentIds = [];
    const enrolmentData = [];
    allEnrolments.forEach(enrolment => {
      if (enrolment.userId === userId) {
        stateIds.push(`${userId}_${enrolment.contentId}`);
        contentIds.push(enrolment.contentId);
        enrolmentData.push(enrolment);
      }
    });
    contentIds = uniq(contentIds.concat(recommendedCSIds));
    // const missingStatesForRecommendation = getMissingStatesForRecommendedContent(recommendedCSIds, userId);
    const [stateData, contentSettings] = await Promise.all([
      LocalStoreService.getMultipleStatesById(stateIds),
      LocalStoreService.getContentSettingsByIds(contentIds)
    ]);
    homePageResponse = {
      enrolmentData,
      stateData,
      contentSettings
    };
  }
  return homePageResponse;
}

export async function fetchHomePageDataRemotely(
  userId,
  companyId,
  parentCompanyId,
  customisedContentIds = []
) {
  const includeRecommended =
    window._currentUserInfo &&
    window._currentUserInfo.company &&
    window._currentUserInfo.company.includeRecommendation;

  /* window.analytics &&
    window.analytics.track('HOMEPAGE_FETCH_DATA', {
      user: userId,
      dataType: DataType.homePage,
      companyID: companyId
    }); */
  const response = await request(
    `${LYEARN_HOME_PAGE_DATA}`,
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify({
        userId,
        dataType: DataType.homePage,
        shouldMergeContentSettings: true,
        shouldIncludeRecommendedContent: includeRecommended,
        companyId: companyId || user.companyId(),
        parentCompanyId: parentCompanyId || user.parentCompanyId(),
        customisedContentIds
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  let homePageResponse;
  let customisedContents;
  if (response.ok) {
    homePageResponse = await response.json();
    customisedContents = homePageResponse.customisedContents;
    if (
      !homePageResponse.stateData ||
      !homePageResponse.contentSettings ||
      !homePageResponse.enrolmentData
    ) {
      console.log('Data missing in homepage query ', homePageResponse);
    }
    await Promise.all([
      saveStatesNotInDataSync(homePageResponse.stateData),
      homePageResponse.contentSettings && homePageResponse.contentSettings.length
        ? LocalStoreService.saveAllContentSettings(homePageResponse.contentSettings)
        : Promise.resolve(),
      saveEnrolmentsNotInDataSync(homePageResponse.enrolmentData)
    ]);
  }
  homePageResponse = await fetchHomePageDataLocally(userId);
  homePageResponse.customisedContents = customisedContents;
  return homePageResponse;
}

/*                        PATH                            */

export async function getStatesEnrolmentsContentSettingsForPathOfferLocally(pathOfferId, userId) {
  const pathContentSetting = await LocalStoreService.getContentSetting(pathOfferId);

  if (!pathContentSetting) {
    // eslint-disable-next-line no-use-before-define
    return getStatesEnrolmentsContentSettingsForPathOfferRemotely(pathOfferId, userId);
  }

  const childrenOfferIds = pathContentSetting.offers.map(offer => offer.offerId);
  const allOfferIds = [pathOfferId, ...childrenOfferIds];

  const allOfferStateKeys = allOfferIds.map(offerId => `${userId}_${offerId}`);

  const [contentSettings, states, enrolments] = await Promise.all([
    LocalStoreService.getContentSettingsByIds(childrenOfferIds),
    LocalStoreService.getMultipleStatesById(allOfferStateKeys),
    LocalStoreService.getMultipleEnrolmentsById(allOfferStateKeys)
  ]);
  return {
    contentSettings: filter([...contentSettings, pathContentSetting]),
    states: filter(states),
    enrolments: filter(enrolments)
  };
}

export async function getStatesEnrolmentsContentSettingsForPathOfferRemotely(pathOfferId, userId) {
  try {
    const response = await fetchContentData(pathOfferId, userId, AssetType.curriculumOffer, false);
    if (!response.ok) {
      const responseBody = await response.body();
      throw new Error('Error: ' + responseBody);
    }
    const { contentSettings, states, enrolments } = await response.json();
    await Promise.all([
      contentSettings && contentSettings.length
        ? LocalStoreService.saveAllContentSettings(contentSettings)
        : Promise.resolve(),
      states && states.length ? saveStatesNotInDataSync(states) : Promise.resolve(),
      enrolments && enrolments.length ? saveEnrolmentsNotInDataSync(enrolments) : Promise.resolve()
    ]);
    return getStatesEnrolmentsContentSettingsForPathOfferLocally(pathOfferId, userId);
  } catch (error) {
    console.log('Error fetching path offer data remotely');
    window.location.href = '/#/learner';
    SnackBarHelper.showSnackbar({
      label: 'Error in fetching PATH details. Please contact support.',
      duration: 7
    });
    window.analytics &&
      window.analytics.track('ERROR_IN_FETCH_PATH_REMOTELY', {
        pathOfferId,
        error: error.message,
        errorType: error.type,
        errorStack: error.stack
      });
    console.log(error);
    throw error;
  }
}

/*                        CHANNELS                            */

export async function fetchChannelDataLocally(userId) {
  const allContentSettings = await LocalStoreService.getAllContentSetting();
  const channel = [];
  const channelCard = [];
  const stateIds = [];
  allContentSettings.forEach(contentSetting => {
    if (contentSetting.modelType === 'channel') {
      channel.push(contentSetting);
    } else if (contentSetting.modelType === 'channelCard') {
      channelCard.push(contentSetting);
      stateIds.push(`${userId}_${contentSetting.contentId}`);
    }
  });
  const states = await LocalStoreService.getMultipleStatesById(stateIds);
  return {
    channel,
    channelCard,
    states
  };
}

export async function fetchChannelLocally(assetId, userId) {
  let { channel, channelCard, states } = await fetchChannelDataLocally(userId);
  channel = [channel.find(data => data.contentId === assetId)];
  channelCard = channelCard.filter(card => card.rootOfferId === assetId);
  states = states.filter(s => !!s).filter(state => state.rootContentId_contentId.includes(assetId));
  return {
    channel,
    channelCard,
    states
  };
}

export async function fetchChannelRemotely(channelId, userId) {
  let channelData;
  const response = await request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        assetData: [
          {
            assetType: AssetType.channelPage,
            channelId
          }
        ],
        userId
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  if (response.ok) {
    channelData = await response.json();
    channelData.channel && LocalStoreService.saveAllContentSettings(channelData.channel);
    channelData.channelCard && LocalStoreService.saveAllContentSettings(channelData.channelCard);
    channelData.states && saveStatesNotInDataSync(channelData.states);
  }
  return channelData;
}

export async function fetchChannelDataRemotely(userGroupIds, userId) {
  let channelData;
  const response = await request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        assetData: [
          {
            userGroupIds,
            assetType: AssetType.channels,
            channelUserId: userId
          }
        ]
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
  if (response.ok) {
    channelData = await response.json();
    channelData.channel && LocalStoreService.saveAllContentSettings(channelData.channel);
    channelData.channelCard && LocalStoreService.saveAllContentSettings(channelData.channelCard);
    // channelData.states && LocalStoreService.saveAllContentSettings(channelData.states);
    channelData.states && saveStatesNotInDataSync(channelData.states);
  }
  return channelData;
}

function fetchChannelContentData(assetId, userId, assetType, channelId) {
  return request(
    `${LYEARN_COMPLETE_DATA}`,
    {
      method: 'POST',
      headers: { 'Content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        userId,
        assetData: [
          {
            assetId,
            assetType,
            channelId
          }
        ]
      })
    },
    {
      dontAppendApiEndpointQS: true
    }
  );
}

export function formatChannelCardStates(states) {
  return states.map(state => ({
    ...state,
    sessionId: window.channelSessionId
  }));
}

export async function fetchChannelCardStateLocally(channelCardId, userId) {
  const state = await LocalStoreService.getStateByUserIdContentId(userId, channelCardId);
  return state ? [state] : [];
}

export async function fetchChannelCardState(
  channelCardId,
  userId,
  channelId,
  assetType = 'CHANNEL_CARD_PAGE'
) {
  let state;
  let refetch = true;
  state = await LocalStoreService.getStateByUserIdContentId(userId, channelCardId);
  if (state && state.sessionId === window.channelSessionId) {
    refetch = false;
  }
  if (refetch || !state) {
    const response = await fetchChannelContentData(channelCardId, userId, assetType, channelId);
    if (response.ok) {
      const data = await response.json();
      const states = data.states ? [data.states] : [];
      await saveStatesNotInDataSync(formatChannelCardStates(states));
      state = await fetchChannelCardStateLocally(channelCardId, userId);
      return state;
    }
  }
  return [state];
}

/*                        ILT                            */

export async function getILTDataLocally(assetId, userId) {
  let states;
  let enrolments;
  const [fetchedStates, enrolment, contentSettings] = await Promise.all([
    // getOfferByIdLocally(assetId),
    LocalStoreService.getStatesForAsset(userId, assetId),
    LocalStoreService.getEnrolment(userId, assetId),
    LocalStoreService.getContentSettingsByIds([assetId])
  ]);
  if (fetchedStates) {
    states = fetchedStates;
    enrolments = [enrolment];
  }
  return {
    activity: contentSettings[0],
    contentUserData: {
      sessionDetails: [],
      states,
      enrolments,
      contentSettings,
      sessionTeacherDetails: [],
      prerequisiteData: []
    }
  };
}

export async function getILTContentSettingsLocally(assetId) {
  const contentSettings = await LocalStoreService.getContentSetting(assetId);
  return {
    offer: contentSettings
  };
}

const fetchILTSessionTeachers = activityId =>
  request('/graphql', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    method: 'POST',
    body: JSON.stringify({
      operationName: null,
      query: `
        {
          activity(id: "${activityId}") {
            sessionTeachers {
              sessionId
              teachers {
                pid
                name
              }
            }
          }
        }
      `,
      variables: null
    })
  }).then(response => response.json());

export async function getILTDataRemotelyForGuestUser(
  assetId,
  userId,
  assetType = AssetType.liveTraining,
  children = false
) {
  const contentUserData = await fetchContentDataGuest(assetId, userId, assetType, children).then(
    response => response.json()
  );
  contentUserData.sessionTeacherDetails = [];
  return {
    activity: contentUserData.contentSettings[0],
    contentUserData
  };
}

export async function getILTSessionTeachers(assetId) {
  const sessionTeacherResponse = await fetchILTSessionTeachers(assetId);
  return (
    (sessionTeacherResponse.data.activity &&
      sessionTeacherResponse.data.activity.sessionTeachers) ||
    []
  );
}

export async function getILTDataRemotely(
  assetId,
  userId,
  assetType = AssetType.liveTraining,
  children = false
) {
  try {
    const [, response, sessionTeacherResponse] = await Promise.all([
      getOfferFromS3(assetId),
      fetchContentData(assetId, userId, assetType, children),
      fetchILTSessionTeachers(assetId)
    ]);
    if (response.ok) {
      const contentUserData = await response.json();
      const enrolmentsToStore = [];
      const statesToStore = [];
      const contentSettingsToStore = [];

      contentUserData.enrolments.forEach(enrolment => enrolmentsToStore.push(enrolment));
      contentUserData.states.forEach(state => statesToStore.push(state));
      contentUserData.contentSettings.forEach(content => contentSettingsToStore.push(content));

      if (contentUserData.prerequisiteData) {
        contentUserData.prerequisiteData.enrolments.forEach(enrolment =>
          enrolmentsToStore.push(enrolment)
        );
        contentUserData.prerequisiteData.states.forEach(state => statesToStore.push(state));
        contentUserData.prerequisiteData.contentSettings.forEach(content =>
          contentSettingsToStore.push(content)
        );
      }

      if (contentUserData && contentUserData.enrolments && !contentUserData.enrolments[0]) {
        contentUserData.enrolments = [];
      }

      const promises = [];
      if (enrolmentsToStore.length > 0) {
        promises.push(saveEnrolmentsNotInDataSync(enrolmentsToStore));
      }
      if (statesToStore.length > 0) {
        promises.push(saveStatesNotInDataSync(statesToStore));
      }
      if (contentSettingsToStore.length > 0) {
        promises.push(LocalStoreService.saveAllContentSettings(contentSettingsToStore));
      }
      await Promise.all(promises);
      const data = await getILTDataLocally(assetId, userId);
      data.contentUserData.sessionDetails = contentUserData.sessionDetails;
      data.contentUserData.sessionTeacherDetails =
        sessionTeacherResponse.data.activity.sessionTeachers || [];
      return data;
    }
  } catch (error) {
    console.log('Error ->', error);
    return null;
  }
  return null;
}

/*                        LEARNER CHECKPOINT                            */

export async function getLearnerCheckpointDataLocally(assetId, userId) {
  const [activity, enrolments, states, allStateInteractions, contentSettings] = await Promise.all([
    getOfferByIdLocally(assetId),
    LocalStoreService.getEnrolment(userId, assetId),
    LocalStoreService.getStateByUserIdContentId(userId, assetId),
    LocalStoreService.getAllStateInteractions(),
    LocalStoreService.getContentSettingsByIds([assetId])
  ]);
  const stateInteractions = allStateInteractions.filter(
    stateInteraction =>
      stateInteraction.userId === userId &&
      stateInteraction.contentId === assetId &&
      !stateInteraction.deleted
  );
  return {
    activity,
    contentUserData: {
      enrolments,
      states,
      stateInteractions,
      contentSettings
    }
  };
}

export async function getLearnerCheckpointDataRemotely(
  assetId,
  userId,
  assetType = AssetType.checkpoint,
  children = false
) {
  try {
    await fetchLatestOfferFromCloudfront(assetId);
    const response = await fetchContentData(assetId, userId, assetType, children);
    if (response.ok) {
      const {
        states,
        curriculumStates,
        enrolments,
        stateInteractions,
        contentSettings
      } = await response.json();
      const enrolment = enrolments.find(e => e.contentId === assetId);

      const curriculumIds = enrolment && enrolment.parentIds ? enrolment.parentIds : [];
      const curriculumEnrolments = enrolments.filter(e => curriculumIds.includes(e.contentId));
      const curriculumIdVersionIds = curriculumEnrolments.map(e => ({
        versionId: e.versionId,
        offerId: e.contentId
      }));
      await getMultipleOfferByIdAndVersionIdsRemotely(curriculumIdVersionIds);
      const promises = [];
      contentSettings.length &&
        promises.push(LocalStoreService.saveAllContentSettings(contentSettings));
      states.length && promises.push(saveStatesNotInDataSync(states));
      enrolments.length && promises.push(saveEnrolmentsNotInDataSync(enrolments));
      curriculumStates.length && promises.push(saveStatesNotInDataSync(curriculumStates));
      stateInteractions.length &&
        promises.push(saveStateInteractionsNotInDataSync(stateInteractions));
      await Promise.all(promises);
      return getLearnerCheckpointDataLocally(assetId, userId);
    }
  } catch (error) {
    console.error('Error -> ', error);
    return null;
  }
  return null;
}

/*                        MANAGER CHECKPOINT                            */

export async function getManagerCheckpointDataLocally(assetId) {
  const [activity, allStateInteractions, allStates] = await Promise.all([
    getOfferByIdLocally(assetId),
    LocalStoreService.getAllStateInteractions(),
    LocalStoreService.getAllStates()
  ]);
  const userStates = [];
  allStates.forEach(state => {
    if (state.contentId === assetId) {
      userStates.push(state);
    }
  });
  const managerStateInteractions = allStateInteractions.filter(s => {
    if (s.contentId === assetId) {
      return true;
    }
    return false;
  });
  return {
    activity,
    contentUserData: {
      userStates,
      managerStateInteractions
    }
  };
}

export async function getManagerCheckpointDataRemotely(
  assetId,
  userId,
  assetType = AssetType.managerCheckpoint,
  children = false
) {
  try {
    await fetchLatestOfferFromCloudfront(assetId);
    const { manageIds, allGroupIds } = await getUserManagesIds(userId);
    const response = await fetchContentData(
      assetId,
      userId,
      assetType,
      children,
      manageIds,
      allGroupIds
    );
    if (response.ok) {
      const contentUserData = await response.json();
      await Promise.all([
        saveStatesNotInDataSync(contentUserData.userStates),
        saveEnrolmentsNotInDataSync(contentUserData.userEnrolments),
        saveStateInteractionsNotInDataSync(contentUserData.managerStateInteractions)
      ]);
      return getManagerCheckpointDataLocally(assetId);
    }
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
  return null;
}

export async function getContentSettingsForReview(userId) {
  let awaitingReviewContentSettings = [];
  const { manageIds } = await getUserManagesIds(userId);
  try {
    const response = await request(
      `${LYEARN_HOME_PAGE_DATA}`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify({
          userId,
          manageIds,
          dataType: DataType.checkpointAwaitingReview
        })
      },
      {
        dontAppendApiEndpointQS: true
      }
    );
    if (response.ok) {
      const data = await response.json();
      awaitingReviewContentSettings = data.contentSettings;
    }
  } catch (err) {
    console.log('Error in getContentSettingsForReview', err);
  }
  return awaitingReviewContentSettings;
}
